class Lightbox extends HTMLElement {
  static observedAttributes = ['open'];

  constructor() {
    super();

    this.selectors = {
      classes: {
        modal: {
          close: 'lightbox-modal-close',
        },
        swiper: {
          wrapper: 'swiper-wrapper',
        },
      },
      attributes: {
        lightbox: {
          trigger: 'lightbox-trigger',
          close: 'lightbox-close',
        },
      },
    };

    this.lightboxSwiper = null;
  }

  init() {
    this.addEventListener('click', this.handleEvent.bind(this));
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.lightboxSwiper) {
      this.lightboxSwiper.destroy();
      this.lightboxSwiper = null;
    }
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      if (newValue !== null) {
        this.setAttribute('aria-hidden', 'false');
      } else {
        this.setAttribute('aria-hidden', 'true');
      }
    }
  }

  showLightbox() {
    this.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.maxHeight = '100vh';
    document.addEventListener('keydown', this.handleKeyDown);
  }

  closeLightbox() {
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
    document.documentElement.style.maxHeight = '';
    document.removeEventListener('keydown', this.handleKeyDown);
    this.remove();
  }

  createSwiperSlide(src) {
    const slideNode = document.createElement('div');
    slideNode.classList.add('swiper-slide', 'lightbox-slide');

    const zoomNode = document.createElement('div');
    zoomNode.classList.add('swiper-zoom-container');

    let formattedSource = new URL(src, window.location.origin);
    if (parseInt(formattedSource.searchParams.get('width') || '0', 10) < 2000) {
      formattedSource.searchParams.set('width', 2000);
    }

    const slideImg = new Image();
    slideImg.src = formattedSource.href;
    zoomNode.appendChild(slideImg);
    slideNode.appendChild(zoomNode);

    return slideNode;
  }

  initSwiper() {
    // Ensure Swiper is available
    const SwiperClass = window.Swiper || window.swiper;
    if (!SwiperClass) {
      console.warn('Lightbox: Swiper library not found');
      return;
    }

    // Add new Swiper modules to this array (and to the import in snippets/javascript.liquid)
    // if this lightbox is extended to use more Swiper features.
    const swiperModules = window.SwiperModules || {};
    this.lightboxSwiper = new SwiperClass(this, {
      modules: [swiperModules.Navigation, swiperModules.Zoom, swiperModules.Keyboard].filter(Boolean),
      slidesPerView: 1,
      spaceBetween: 16,
      direction: 'horizontal',
      rtl: false,
      navigation: {
        nextEl: '.lightbox-modal-navigation.swiper-button-next',
        prevEl: '.lightbox-modal-navigation.swiper-button-prev',
      },
      zoom: {
        maxRatio: 3,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      on: {
        afterInit: () => {
          this.removeAttribute('modal-loading');
        },
      },
    });
  }

  handleEvent(e) {
    const closeElement = e.target.closest('[' + this.selectors.attributes.lightbox.close + ']');
    if (closeElement) {
      this.closeLightbox();
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      this.closeLightbox();
    }
  }

  openLightbox(lightboxTrigger) {
    const lightboxGroup = lightboxTrigger.dataset.lightboxGroup;
    const triggerSource = lightboxTrigger.dataset.lightboxSrc;

    const lightboxSources = this.getLightboxSources(lightboxGroup, triggerSource);

    this.loadSwiperSlides(lightboxSources, triggerSource);

    this.showLightbox();
  }

  getLightboxSources(lightboxGroup, triggerSource) {
    const lightboxSources = [];

    if (lightboxGroup) {
      const groupTriggers = document.querySelectorAll(
        `[lightbox-trigger][data-lightbox-group="${lightboxGroup}"]`
      );
      groupTriggers.forEach((groupTrigger) => {
        const src = groupTrigger.dataset.lightboxSrc;
        if (src) {
          lightboxSources.push(src);
        }
      });
    } else if (triggerSource) {
      lightboxSources.push(triggerSource);
    }

    return lightboxSources;
  }

  loadSwiperSlides(lightboxSources, triggerSource) {
    const slideElements = [];
    const swiperWrapper = this.querySelector(`.${this.selectors.classes.swiper.wrapper}`);

    if (!swiperWrapper) return;

    swiperWrapper.innerHTML = '';

    let selectedIndex = 0;

    lightboxSources.forEach((src, index) => {
      slideElements.push(this.createSwiperSlide(src));
      if (src === triggerSource) selectedIndex = index;
    });

    swiperWrapper.append(...slideElements);

    if (!this.lightboxSwiper) {
      this.initSwiper();
    }

    if (this.lightboxSwiper) {
      this.lightboxSwiper.update();
      this.lightboxSwiper.slideTo(selectedIndex, 0, true);
    }
  }
}

if (!customElements.get('lightbox-modal')) {
  customElements.define('lightbox-modal', Lightbox);

  async function createLightbox(lightboxTrigger) {
    return fetch(window.Shopify.routes.root + '?section_id=qf-lightbox')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((htmlString) => {
        const html = new DOMParser().parseFromString(htmlString, 'text/html');
        const lightboxElement = html.querySelector('lightbox-modal');

        if (lightboxElement) {
          document.body.appendChild(lightboxElement);
          lightboxElement.openLightbox(lightboxTrigger);
        }
      })
      .catch((error) => {
        console.error('Lightbox error:', error);
        alert('Failed to load image gallery. Please try again.');
      });
  }

  document.body.addEventListener('click', (e) => {
    const trigger = e.target.closest('[lightbox-trigger]');
    if (trigger && !trigger.disabled) {
      e.preventDefault();
      trigger.disabled = true;
      createLightbox(trigger).then(() => {
        trigger.disabled = false;
      });
    }
  });
}


